import React, {useEffect, useMemo, useState} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {setConstructionPlanBrush, showToast} from '../../../redux/actions';
import {selectHexes, selectRound} from '../../../redux/reducers/sharedStateReducer/selectors';
import {
  selectConstructionPlanBrushToken,
  selectConstructionPlanEntries
} from '../../../redux/reducers/privateStateReducer/selectors';
import {getConstructionPlanPlayer} from '../../../utils/gameState/construction/planning/getConstructionPlanContext';
import {
  buildConstructionPlanPendingBuildings,
  buildConstructionPlanViewPlayer
} from '../../../utils/gameState/construction/planning/buildConstructionPlanningViewState';
import {
  getDefaultBuildingToken,
  useBuildingGroups,
  useBuildings,
  useIsAllBuildingsShown
} from '../NewBuildingToolbar/NewBuildingToolbar';
import NewBuildingToolbarView from '../NewBuildingToolbar/NewBuildingToolbarView';
import {checkIfAnyBuildingToken} from '../../../utils/buildings/checkIfAnyBuildingToken';
import {checkIfSpecialBuilding} from '../../../utils/buildings/checkIfSpecialBuilding';
import {usePrev} from '../../../hooks/react/usePrev';
import {REGISTRY_SINGLETON} from '../../../scenes/Registry';
import {
  TEXT_MINE_EXPLORATION_NOT_PLANNABLE,
  TEXT_NOT_ALLOWED_FIRST_ROUND_BUILDINGS
} from '../../../constants/text/construction';
import {MINE_EXPLORATION} from '../../../configs/factions/dwarves';

/**
 * Панель строений для плана строительства в чужой ход (см.
 * getConstructionPlanContext): тот же список, что в свой ход, но для самого
 * игрока и с ценами с учетом уже запланированного. Выбор строения — только в
 * план (setConstructionPlanBrush). Отдельная панель, а не режим панели своего
 * хода: у той в начале хода свои действия (жетоны элементов уходят на сервер,
 * выбор строения по смене ходящего игрока), и в чужой ход ни одно из них
 * срабатывать не должно
 */
const PlanningBuildingToolbar = () => {
  const dispatch = useDispatch();
  const player = useSelector(getConstructionPlanPlayer);
  const currentRound = useSelector(selectRound);
  const hexes = useSelector(selectHexes);
  const brushToken = useSelector(selectConstructionPlanBrushToken);
  const entries = useSelector(selectConstructionPlanEntries);
  // Список — по уже выбранному в план строению: панель пересоздается после
  // инспекции ячейки, а выбор в плане остается
  const [isSpecial, setIsSpecial] = useState(() => checkIfSpecialBuilding(brushToken));
  const prevIsSpecial = usePrev(isSpecial);

  const pendingBuildings = useMemo(
    () => (player ? buildConstructionPlanPendingBuildings(entries, player, hexes) : []),
    [entries, player, hexes]
  );
  // Разведка рудника у гномов в списке остается, как в свой ход, — выбрать ее
  // в план нельзя (см. handleSelect)
  const isAllShown = useIsAllBuildingsShown();
  const buildings = useBuildings(isSpecial, player, currentRound, pendingBuildings, isAllShown);
  const buildingGroups = useBuildingGroups(buildings, isAllShown);

  // Строение по умолчанию — когда ничего не выбрано (панель только появилась)
  // и при переключении списка обычных/специальных строений, как в свой ход
  useEffect(() => {
    // В списке «все строения» isSpecial только следит за выбором — это не
    // переключение списка (см. NewBuildingToolbar)
    const isListToggled = !isAllShown && prevIsSpecial !== undefined && prevIsSpecial !== isSpecial;
    if (!isListToggled && brushToken) {
      return;
    }
    const defaultToken = getDefaultBuildingToken(buildings, currentRound);
    if (defaultToken && defaultToken !== brushToken) {
      dispatch(setConstructionPlanBrush(defaultToken));
    }
  }, [isSpecial, prevIsSpecial, brushToken, buildings, currentRound, dispatch, isAllShown]);

  const handleSelect = (tokenId, isDisabledFirstRound) => {
    if (isDisabledFirstRound) {
      dispatch(showToast(TEXT_NOT_ALLOWED_FIRST_ROUND_BUILDINGS, 'warning', 5000));
      return;
    }
    // Разведку рудника не запланировать: ее исход решает бросок кубика, а
    // бросают только в свой ход
    if (tokenId === MINE_EXPLORATION) {
      dispatch(showToast(TEXT_MINE_EXPLORATION_NOT_PLANNABLE, 'warning', 5000));
      return;
    }
    REGISTRY_SINGLETON.getSoundManager()?.playTokenClick(tokenId);

    if (checkIfAnyBuildingToken(tokenId)) {
      dispatch(setConstructionPlanBrush(null));
      setIsSpecial(!isSpecial);
      return;
    }
    if (isAllShown) {
      setIsSpecial(checkIfSpecialBuilding(tokenId));
    }
    dispatch(setConstructionPlanBrush(tokenId));
  };

  if (!player) {
    return null;
  }

  return (
    <NewBuildingToolbarView
      buildings={buildings}
      buildingGroups={buildingGroups}
      selectedToken={brushToken}
      currentRound={currentRound}
      availableCoins={buildConstructionPlanViewPlayer(player, pendingBuildings).coins}
      onSelect={handleSelect}
    />
  );
};

export default PlanningBuildingToolbar;
